import { apiClient } from './client';
import type { RPCResponse, LoginResult, StudiesResult, User, Study, DicomObject, Message, SiteRecord, SystemSummary, PatientRecord, Rule, HL7Connection, HL7Message, ScheduleEntry, Job, FaxQueueItem, NotificationRecord, StudyNote, StudyLogEntry, UpdateRecord, ReportBean, MacroBean, LockBean, ReportStatus, EditPatientParams, PeerReviewParams, StudyLocationResult, DeleteObjectsParams, SavedSearch, SavedSearchInput, CallContact, OrderInfo, PatientBean, StorageNote, PatientDiscoveryResult, ProcedureDefinition, HL7DumpBean, SessionLogEntry, PacsLogEntry, StudyTransactionEntry, SystemActivityReportData, ReadingLocation, MissedSlaCategory, CDPreparationOptions, DownloadProgressInfo, PatientMappingEntry, PatientObjectItem, RemotePatientResult, InsuranceCompanyBean, ProtocolBean, BritProtocol, QCCommandRequest, QCCommandResult, SharingBean, MacrosExportResult, AIValidationMatch, FireDrillReport, FireDrillHistoryEntry, GridExportRequest, CommandMessage, TestConnectionResult, LoginConfig, WhereClause, MergedPatientData } from './types';
import type { AxiosRequestConfig } from 'axios';

let idCounter = 1;

/**
 * Typed JSON-RPC 2.0 caller.
 *
 * @param method - RPC method name (e.g. "login", "getStudiesPaged")
 * @param params - method parameters
 * @param auth   - optional Basic Auth for login
 * @param signal - optional AbortSignal for cancellation
 */
export async function rpcCall<T>(
  method: string,
  params: unknown = {},
  auth?: { user: string; pass: string },
  signal?: AbortSignal,
): Promise<T> {
  const payload = {
    jsonrpc: '2.0',
    method,
    params,
    id: idCounter++,
  };

  const config: AxiosRequestConfig = {};
  if (auth) {
    config.auth = { username: auth.user, password: auth.pass };
  }
  if (signal) {
    config.signal = signal;
  }

  const response = await apiClient.post<RPCResponse<T>>('', payload, config);

  if (response.data.error) {
    throw new Error(response.data.error.message);
  }

  // Capture session ID if present (login response)
  if (response.data.sid) {
    localStorage.setItem('brit_sid', response.data.sid);
  }

  return response.data.result as T;
}

/** High-level API methods matching JSONRPCFilter.java */
export const api = {
  login: (user: string, pass: string) =>
    rpcCall<LoginResult>('login', { genToken: true }, { user, pass }),

  logout: () => {
    localStorage.removeItem('brit_sid');
    return rpcCall('logout').catch(() => {
      /* ignore logout errors */
    });
  },

  validateSession: () =>
    rpcCall<User>('validateSession'),

  getStudiesPaged: (
    start = 0,
    limit = 100,
    filters?: Record<string, unknown>,
    sort?: { field: string; direction: 'ASC' | 'DESC' }[],
    signal?: AbortSignal,
  ) =>
    rpcCall<StudiesResult>(
      'getStudiesPaged',
      {
        start,
        limit,
        ...filters,
        sort,
      },
      undefined,
      signal,
    ),

  getCounters: () =>
    rpcCall<Record<string, number>>('getCounters'),

  /** Hold a study */
  holdStudy: (objKey: string, reason: string) =>
    rpcCall<boolean>('holdStudy', { objKey, reason }),

  /** Unhold a study */
  unholdStudy: (objKey: string) =>
    rpcCall<boolean>('unholdStudy', { objKey }),

  /** Mark study as urgent */
  markUrgent: (objKey: string, isUrgent: boolean, reason?: string) =>
    rpcCall<boolean>('markUrgent', { objKey, isUrgent, reason }),

  /** Update study status */
  updateStudyStatus: (objKey: string, status: string) =>
    rpcCall<string>('updateStudyStatus', { objKey, status }),

  /** Delete a study */
  deleteStudy: (objKey: string) =>
    rpcCall<boolean>('deleteStudy', { objKey }),

  /** Get a single study's details */
  getStudy: (objKey: string) =>
    rpcCall<Study>('getStudy', { objKey }),

  /** Get DICOM objects for a study */
  getObjects: (stuKey: string) =>
    rpcCall<{ records: DicomObject[]; total: number }>('getObjects', { stuKey }),

  /** Mark messages as read */
  markRead: (objKey: string) =>
    rpcCall<boolean>('markRead', { objKey }),

  /** Get reports/documents for a study */
  getDocument: (studyKey: string, documentType = 'REPORT') =>
    rpcCall<{ documentContent?: string; documents?: string[] }>('getDocument', {
      documentType,
      studyKey,
    }),

  /** Get system summary / dashboard stats */
  getSystemSummary: () =>
    rpcCall<SystemSummary>('getSystemSummary'),

  /** Get all messages */
  getMessages: () =>
    rpcCall<{ records: Message[] }>('getMessages'),

  /** Send a message */
  sendMessage: (to: string, subject: string, body: string) =>
    rpcCall<boolean>('sendMessage', { to, subject, body }),

  /** Get paged users list with optional search/filter params */
  getUsersPaged: (start = 0, limit = 100, filters?: {
    userType?: string; searchName?: string; searchPhone?: string;
    searchAddress?: string; searchIP?: string; searchLocation?: string;
    searchSite?: string; searchEmail?: string; searchAetitle?: string;
    searchInactive?: boolean; searchLocationType?: string;
  }) =>
    rpcCall<{ total: number; records: User[] }>('getUsersPaged', { start, limit, ...filters }),

  /** Get all sites */
  getSites: () =>
    rpcCall<{ records: SiteRecord[] }>('getSites'),

  /** Add or modify a user. On create, returns { success: true, objKey: string }. On update, returns boolean. */
  addModifyUser: (userData: Record<string, unknown>) =>
    rpcCall<boolean | { success: boolean; objKey: string }>('addModifyUser', userData),

  /** Delete a user — backend expects siteId + userId, NOT objKey */
  deleteUser: (siteId: string, userId: string) =>
    rpcCall<boolean>('deleteUser', { siteId, userId }),

  /** Get patients (paged) */
  getPatients: (start = 0, limit = 100, search?: string) =>
    rpcCall<{ total: number; records: PatientRecord[] }>('getPatients', { start, limit, search }),

  /** Get a single patient */
  getPatient: (objKey: string) =>
    rpcCall<PatientRecord>('getPatient', { objKey }),

  /** Add or modify a patient */
  addModifyPatient: (patientData: Record<string, unknown>) =>
    rpcCall<boolean>('addModifyPatient', patientData),

  /** Delete a patient */
  deletePatient: (objKey: string) =>
    rpcCall<boolean>('deletePatient', { objKey }),

  /** Update a user attribute (backend expects 'attr' and 'value') */
  setUserAttribute: (attr: string, value: string) =>
    rpcCall<boolean>('setUserAttribute', { attr, value }),

  /** Get routing rules */
  getRules: () =>
    rpcCall<{ records: Rule[]; total: number }>('getRules'),

  /** Add or update a routing rule */
  updateRule: (rule: Record<string, unknown>) =>
    rpcCall<boolean>('updateRule', rule),

  /** Delete a routing rule */
  deleteRule: (objKey: string) =>
    rpcCall<boolean>('deleteRule', { objKey }),

  /** Get HL7 queue connections */
  getHL7Queue: () =>
    rpcCall<{ records: HL7Connection[] }>('getHL7Queue'),

  /** Get HL7 messages for a connection */
  getHL7QueueMessages: (connectionId: string, offset = 0, limit = 100) =>
    rpcCall<{ records: HL7Message[]; total: number }>('getHL7QueueMessages', { connectionId, offset, limit }),

  /** Resend/reprocess HL7 messages */
  resendHL7Messages: (connectionId: string, messageIds?: string[], isOutbound = true) =>
    rpcCall<{ success: boolean; count: number }>('resendHL7Messages', { connectionId, messageIds, isOutbound }),

  /** Delete (cancel) HL7 messages */
  deleteHL7Messages: (connectionId: string, messageIds?: string[]) =>
    rpcCall<{ success: boolean; count: number }>('deleteHL7Messages', { connectionId, messageIds }),

  /** Set HL7 queue connection state (start/stop/pause_on/pause_off) */
  setHL7QueueState: (connectionId: string, siteObjKey: string, isOutbound: boolean, state: string) =>
    rpcCall<boolean>('setHL7QueueState', { connectionId, siteObjKey, isOutbound, state }),

  /** Get schedule entries for a date */
  getSchedule: (date: string, siteId?: string, modality?: string) =>
    rpcCall<{ records: ScheduleEntry[] }>('getSchedule', { date, siteId, modality }),

  /** Get background jobs */
  getJobs: () =>
    rpcCall<{ records: Job[]; total: number }>('getJobs'),

  /** Stop one or more jobs */
  stopJobs: (jobs: string[]) =>
    rpcCall<{ records: Job[]; total: number }>('stopJobs', { jobs }),

  /** Start one or more jobs */
  startJobs: (jobs: string[]) =>
    rpcCall<{ records: Job[]; total: number }>('startJobs', { jobs }),

  /** Retry one or more jobs (all items or failed-only) */
  retryJobs: (jobs: string[], failedOnly = false) =>
    rpcCall<{ records: Job[]; total: number }>('retryJobs', { jobs, failedOnly }),

  /** Delete one or more jobs */
  deleteJobs: (jobs: string[]) =>
    rpcCall<{ deleted: number; running: number; message?: string }>('deleteJobs', { jobs }),

  /** Get fax queue items */
  getFaxQueue: (start = 0, limit = 500) =>
    rpcCall<{ records: FaxQueueItem[]; total: number }>('getFaxQueue', { start, limit }),

  /** Resend selected faxes */
  resendFaxes: (keys: string[]) =>
    rpcCall<{ resent: number }>('resendFaxes', { keys }),

  /** Stop sending selected faxes */
  stopSendingFaxes: (keys: string[]) =>
    rpcCall<string>('stopSendingFaxes', { keys }),

  /** Get active notification runners */
  getNotifications: () =>
    rpcCall<{ records: NotificationRecord[]; total: number }>('getNotifications'),

  /** Add or modify a site — basic fields + details blob for config */
  addModifySite: (data: {
    objKey: string;
    name?: string;
    address?: string;
    contact?: string;
    phone?: string;
    details?: Record<string, unknown>;
  }) => rpcCall<SiteRecord>('addModifySite', data),

  /** Get server log entries */
  getLogs: (level?: string, lines = 200) =>
    rpcCall<{ lines: string[]; file: string }>('getLogs', { level, lines }),

  /** Get JSON user preference */
  getJson: (key: string) =>
    rpcCall<unknown>('getjson', { key }),

  /** Set JSON user preference */
  setJson: (key: string, value: unknown) =>
    rpcCall<boolean>('setjson', { key, value }),

  /** Get PACS properties */
  getProperties: () =>
    rpcCall<Record<string, string>>('getProperties'),

  /** Report viewed event */
  reportViewed: (objKey: string) =>
    rpcCall<boolean>('reportViewed', { objKey }),

  /** Study viewed event */
  studyViewed: (objKey: string) =>
    rpcCall<boolean>('studyViewed', { objKey }),

  /** Open study for viewing */
  openStudy: (stuKey: string) =>
    rpcCall<boolean>('openStudy', { stuKey }),

  /** Close study after viewing (requires user, uid or acc, site) */
  closeStudy: (params: { user: string; uid: string; site: string }) =>
    rpcCall<boolean>('doCloseStudy', params),

  /** Get comparison studies */
  getComparisons: (objKey: string) =>
    rpcCall<{ records: Study[] }>('getComparisons', { objKey }),

  /** Edit a report (save draft, preliminary, etc.) */
  editReport: (params: {
    stuKey: string; objKey?: string; text: string; status?: string;
    rad?: string; resident?: string; tech?: string; transcribe?: string;
    dictObjId?: string; residentReport?: string; residentScore?: number;
    isMarkStudyDictated?: boolean; isNeedsReview?: boolean; imgsPerLine?: number;
    urgentReason?: string; urgentNote?: string; unlink?: boolean;
    reportEvent?: string;
  }) => rpcCall<ReportBean>('editReport', params),

  /** Get list of available report template names */
  getReportTemplateList: () =>
    rpcCall<{ records: string[]; total: number }>('getReportTemplateList'),

  /** Get a rendered report template as HTML */
  getReportTemplate: (name: string, text?: string, stuObjKey?: string) =>
    rpcCall<string>('getReportTemplate', { name, text, stuObjKey }),

  /** Get current user's personal macros */
  getOwnUserMacros: (userObjKey: string) =>
    rpcCall<{ records: MacroBean[]; total: number }>('getOwnUserMacros', { userObjKey }),

  /** Get site-level macros */
  getSiteMacros: (siteObjKey: string) =>
    rpcCall<{ records: MacroBean[]; total: number }>('getSiteMacros', { siteObjKey }),

  /** Save macros for a user */
  saveMacros: (params: {
    userObjKey: string; macros: MacroBean[]; isDel?: boolean; needReplace?: boolean;
  }) => rpcCall<{ records: MacroBean[]; total: number }>('saveMacros', params),

  /** Get study statistics */
  getStudyStatistics: () =>
    rpcCall<Record<string, number>>('getStudyStatistics'),

  /** Update password */
  updatePassword: (oldPassword: string, newPassword: string) =>
    rpcCall<boolean>('updatePassword', { oldPassword, newPassword }),

  /**
   * Add/modify study metadata — used for edit study, mark important, mark follow-up, etc.
   * Params: site, studyUID, and any of status, important, followUp, description, mod, bod, accNum, phyName, discordance
   */
  addModifyStudy: (params: {
    site: string;
    studyUID: string;
    important?: string;
    followUp?: string;
    description?: string;
    mod?: string;
    bod?: string;
    accNum?: string;
    phyName?: string;
    status?: string;
    discordance?: string;
  }) => rpcCall<boolean>('addModifyStudy', params),

  // ── Phase 6: Study actions ──

  /** Assign study to a user (userKey=null to release/unassign) */
  assignStudy: (objKeys: string[], userKey: string | null, assignToMe = false) =>
    rpcCall<boolean>('assignStudy', { objKeys, userKey, assignToMe }),

  /** Get study notes */
  getStudyNotes: (objKey: string) =>
    rpcCall<{ records: StudyNote[]; total: number }>('getStudyNotes', { objKey }),

  /** Add a study note */
  addStudyNote: (objKey: string, text: string, isPrivate = false) =>
    rpcCall<boolean>('addStudyNote', { objKey, text, isPrivate }),

  /** Delete a study note */
  deleteStudyNote: (objKey: string, noteKey: string) =>
    rpcCall<boolean>('deleteStudyNote', { objKey, noteKey }),

  /** Get study audit log */
  getStudyLog: (objKey: string) =>
    rpcCall<{ records: StudyLogEntry[]; total: number }>('getStudyLog', { objKey }),

  /** Send study via DICOM C-STORE */
  sendStudy: (objKey: string, userKey?: string) =>
    rpcCall<boolean>('sendStudy', { objKey, userKey }),

  /** Long-poll for updates (studies, messages, jobs, etc.) */
  getUpdates: (noWait = false) =>
    rpcCall<{ records: UpdateRecord[] }>('getUpdates', { noWait }),

  // ── Phase 6: Report editing ──

  /** Get reports for a study (with addendums) */
  getReports: (stuKey: string) =>
    rpcCall<{ records: ReportBean[]; total: number }>('getReports', { stuKey }),

  /** Sign a report */
  signReport: (stuKey: string, objKey: string, reportEvent?: string) =>
    rpcCall<ReportBean>('signReport', { stuKey, objKey, reportEvent }),

  /** Create or edit an addendum (-1 = new addendum) */
  editAddendum: (params: {
    stuKey: string;
    objKey: string;
    index?: number;
    text: string;
    status?: ReportStatus;
    urgentReason?: string;
    urgentNote?: string;
  }) => rpcCall<ReportBean>('editAddendum', params),

  /** Cancel signing of a report or addendum */
  cancelReportSigning: (stuKey: string, objKey: string, addendumIndex?: number) =>
    rpcCall<ReportBean>('cancelReportSigning', { stuKey, objKey, addendumIndex: addendumIndex ?? -1 }),

  /** Get macros tree (optionally include site macros) */
  getMacros: (siteKey?: string) =>
    rpcCall<{ records: MacroBean[]; total: number }>('getMacros', { siteKey }),

  /** Obtain an exclusive lock on a study (for report editing) */
  lockStudy: (stuKey: string) =>
    rpcCall<LockBean>('lockStudy', { stuKey }),

  /** Release a study lock */
  unlockStudy: (lockKey: string) =>
    rpcCall<boolean>('unlockStudy', { lockKey }),

  /** Complete a study */
  completeStudy: (objKey: string, reason?: string, history?: string, comment?: string) =>
    rpcCall<boolean>('completeStudy', { objKey, reason, history, comment }),

  // ── Phase 6 batch 3: HL7 send, delete, peer review, locations, restore, patient ──

  /** Send reports via HL7 for a study */
  sendReportsViaHL7: (stuKey: string) =>
    rpcCall<number>('sendReportsViaHL7', { stuKey }),

  /** Send orders via HL7 for given study object keys */
  sendOrdersViaHL7: (objKeys: string[]) =>
    rpcCall<number>('sendOrdersViaHL7', { objKeys }),

  /** Send image availability notifications for given study object keys */
  sendImgAvailability: (objKeys: string[]) =>
    rpcCall<boolean>('sendImgAvailability', { objKeys }),

  /** Delete all DICOM images for a study */
  deleteImagesForStudy: (stuKey: string) =>
    rpcCall<boolean>('deleteImagesForStudy', { stuKey }),

  /** Delete specific objects from a study */
  deleteObjects: (params: DeleteObjectsParams) =>
    rpcCall<boolean>('deleteObjects', params),

  /** Mark/update peer review for a study */
  markPeerReview: (params: PeerReviewParams) =>
    rpcCall<boolean>('markPeerReview', params),

  /** Get study location info (admin only) */
  getStudyLocation: (objKey: string) =>
    rpcCall<StudyLocationResult>('getStudyLocation', { objKey }),

  /** Restore a study from a transaction (admin only) */
  restoreStudy: (transactionKey: string) =>
    rpcCall<boolean>('restoreStudy', { transactionKey }),

  /** Edit patient demographics on a study */
  editPatient: (params: EditPatientParams) =>
    rpcCall<boolean>('editPatient', params),

  /** Update discordance on a study */
  updateDiscordance: (params: {
    objKey: string;
    callMade?: boolean;
    actionNeeded?: boolean;
    note?: string;
    callDiagnosis?: string;
    callUrgency?: string;
    radCall?: string;
    viewed?: boolean;
    needResult?: boolean;
  }) => rpcCall<boolean>('updateDiscordance', params),

  /** Mark discordance as viewed */
  discordanceViewed: (objKey: string, userKey?: string) =>
    rpcCall<boolean>('discordanceViewed', { objKey, userKey }),

  /** Generic study attribute setter */
  setStudyAttribute: (objKey: string, attr: string, value: string | null) =>
    rpcCall<string | null>('setStudyAttribute', { objKey, attr, value }),

  /** Share a study with another user */
  shareStudy: (studyKey: string, to: string, message: string) =>
    rpcCall<boolean>('shareStudy', { studyKey, to, message }),

  /** Set arrive-patient status ("W" = Waiting, "R" = Ready, null = clear) */
  arrivePatient: (objKey: string, status: 'W' | 'R' | null) =>
    rpcCall<string | null>('arrivePatient', { objKey, status }),

  /** Mark study with missed SLA reasons */
  markMissedSla: (
    objKey: string,
    reasons: Array<{ category: string; reasons: string[] }>,
    note: string,
  ) => rpcCall<boolean>('markMissedSla', { objKey, reasons, note }),

  // ── Saved Searches ──

  /** Get saved searches for current user */
  getSavedSearches: (userObjKey?: string, includePredefined = true) =>
    rpcCall<SavedSearch[]>('getSavedSearches', { userObjKey, includePredefined }),

  /** Create or update a saved search */
  editSavedSearch: (search: SavedSearchInput, asNew = false) =>
    rpcCall<SavedSearch>('editSavedSearch', { search, asNew }),

  /** Delete a saved search */
  deleteSavedSearch: (searchId: string, userObjKey?: string) =>
    rpcCall<boolean>('deleteSavedSearch', { searchId, userObjKey }),

  // ── Contacts & Discordance ──

  /** Get call contacts for a study (assembled server-side from related users) */
  getStudyContacts: (objKey: string) =>
    rpcCall<{ records: CallContact[]; total: number }>('getStudyContacts', { objKey }),

  /** Look up users by their objKeys */
  getUserByKey: (userKeys: string[]) =>
    rpcCall<{ records: User[]; total: number }>('getUserByKey', { userKeys }),

  // ── Orders ──

  /** Get patient bean (with optional storage notes) */
  getPatientBean: (objKey: string, withStorageNotes = false) =>
    rpcCall<PatientBean>('getPatientBean', { objKey, withStorageNotes }),

  /** Get order info for a study */
  getOrder: (objKey: string) =>
    rpcCall<OrderInfo | null>('getOrder', { objKey }),

  /** Auto-add orders for multiple studies */
  autoAddOrders: (studyObjKeys: string[]) =>
    rpcCall<Study[]>('autoAddOrders', { studyObjKeys }),

  /** Add orders */
  addOrders: (patientKey: string, orders: OrderInfo[], studies: Partial<Study>[]) =>
    rpcCall<Study[]>('addOrders', { patientKey, orders, studies }),

  /** Cancel an order */
  cancelOrder: (objKey: string, type: string, reason: string) =>
    rpcCall<Study>('cancelOrder', { objKey, type, reason }),

  /** Unschedule an order (SCHEDULED → ORDERED) */
  unscheduleOrder: (objKey: string, type?: string, reason?: string) =>
    rpcCall<Study>('unscheduleOrder', { objKey, type, reason }),

  /** Schedule an order (ORDERED → SCHEDULED) */
  scheduleOrder: (objKey: string, date: number, ae?: string, duration?: number) =>
    rpcCall<Study>('scheduleOrder', { objKey, date, ae, duration }),

  /** Reschedule/cancel/unschedule/move an order */
  rescheduleOrder: (params: {
    objKey: string;
    action: 'cancel' | 'unschedule' | 'move';
    type?: string;
    reason?: string;
    date?: number;
    ae?: string;
    duration?: number;
  }) => rpcCall<Study>('rescheduleOrder', params),

  /** Update study notes (general note update) */
  updateStudyNotes: (objKey: string, note: string) =>
    rpcCall<boolean>('updateStudyNotes', { objKey, note }),

  /** Update order insurances */
  updateInsurances: (objKey: string, orderInfo: OrderInfo, patientKey?: string) =>
    rpcCall<OrderInfo | null>('updateInsurances', { objKey, orderInfo, patientKey }),

  /** Save storage options (forbidden deletion types) */
  saveStorageOptions: (params: {
    itemObjKey: string;
    level: 'STUDY' | 'PATIENT';
    reason?: string;
    forbiddenTypes?: string[];
  }) => rpcCall<StorageNote>('saveStorageOptions', params),

  /** Find patients for patient discovery */
  findPatientsDiscovery: (patientObjKey: string) =>
    rpcCall<PatientDiscoveryResult>('findPatientsDiscovery', { patientObjKey }),

  /** Create upload request */
  uploadRequest: (params: {
    studyKey?: string;
    to?: string;
    message?: string;
    reason?: string;
    options?: Record<string, unknown>;
  }) => rpcCall<{ objKey: string; studyKey: string; success: boolean }>(
    'uploadRequest', params,
  ),

  // ── Phase 6.19: Additional admin endpoints ──

  /** Delete a site permanently (Root Admin only) */
  deleteSite: (objKey: string) =>
    rpcCall<boolean>('deleteSite', { objKey }),

  /** Perform a DICOM C-ECHO to a remote server */
  dicomEcho: (aetitle: string, hostname: string, port: number, fromSite?: string) =>
    rpcCall<boolean>('dicomEcho', { aetitle, hostname, port, fromSite }),

  /** Move a user from one site to another */
  moveUser: (userObjKey: string, destSiteObjKey: string, addAcl?: string[], removeAcl?: string[], specialConfig?: string) =>
    rpcCall<User>('moveUser', { userObjKey, destSiteObjKey, addAcl, removeAcl, specialConfig }),

  /** Merge two users (original gets deleted, data transferred to base) */
  mergeUsers: (baseUserKey: string, originalUserKey: string, destSiteObjKey?: string, movePreferences?: boolean) =>
    rpcCall<User>('mergeUsers', { baseUserKey, originalUserKey, destSiteObjKey, movePreferences }),

  /** Get access groups for a site */
  getGroupsBySite: (siteObjKey: string) =>
    rpcCall<{ records: Array<{ objKey: string; id: string; name: string; type: string; acls: string }>; total: number }>(
      'getGroupsBySite', { siteObjKey }),

  /** Dismiss/acknowledge notifications */
  markNotificationRead: (notificationKeys: string[]) =>
    rpcCall<{ success: boolean; count: number }>('markNotificationRead', { notificationKeys }),

  /** Clear all resolved notifications */
  clearResolvedNotifications: () =>
    rpcCall<{ success: boolean }>('clearResolvedNotifications'),

  // ── Phase 6.20: Fire drill, emergency, sample studies, modalities, locations, procedures, access groups ──

  /** Schedule a fire drill for a site */
  scheduleFiredrill: (params: {
    siteObjKey: string; startDate: number; fromTime: number; untilTime: number;
    numRetries: number; retryTime: number; maxCalls: number; roles?: string[];
    nextDay?: boolean; secondary?: boolean; noweekends?: boolean; message?: string;
  }) => rpcCall<{ success: boolean; drillId: string }>('scheduleFiredrill', params),

  /** Schedule an emergency call for a site */
  scheduleEmergencyCall: (params: {
    siteObjKey: string; startDate: number; fromTime: number; untilTime: number;
    numRetries: number; retryTime: number; numCalls: number;
    stopTime?: number; ackTime?: number; callList?: string[]; days: string[];
    reqAck?: boolean; secondary?: boolean; message?: string; method?: string;
  }) => rpcCall<{ success: boolean; drillId: string }>('scheduleEmergencyCall', params),

  /** Load sample DICOM studies into sites */
  loadSampleStudies: (siteObjKeys: string[]) =>
    rpcCall<{ html: string; totalImages: number; results: Array<{ siteName: string; siteObjKey: string; success: number; failed: number }> }>(
      'loadSampleStudies', { siteObjKeys }),

  /** Get modality type strings (optionally for a specific site) */
  getModalities: (siteObjKey?: string) =>
    rpcCall<{ records: string[] }>('getModalities', siteObjKey ? { siteObjKey } : {}),

  /** Get performing entity locations for a site */
  getLocations: (siteObjKey: string) =>
    rpcCall<{ records: Array<{ description: string; siteKey: string; userKey: string; modality: string }> }>(
      'getLocations', { siteObjKey }),

  /** Get procedure definitions for a site */
  getProcedures: (siteObjKey: string) =>
    rpcCall<{ records: Array<{
      procCode: string; codeDesig: string; codeMeaning: string; modality: string;
      bodyPart: string; minTime: number; mrvu: number; laterality: string;
      prepNotes: string; objKey: string; customized: boolean;
    }> }>('getProcedures', { siteObjKey }),

  /** Get access groups for a site */
  getAccessGroupsBySite: (siteObjKey: string) =>
    rpcCall<{ records: Array<{ objKey: string; name: string; id: string; type: string }> }>(
      'getAccessGroups', { siteObjKey }),

  /** Get user roles for a site */
  getUserRolesBySite: (siteObjKey: string) =>
    rpcCall<{ records: Array<{ role: string; acl: string; description: string }> }>(
      'getUserRolesBySite', { siteObjKey }),

  /** Get full per-role permission details for a site */
  getSiteRolePermissions: (siteObjKey: string) =>
    rpcCall<{ roles: Array<{ roleObjKey: string; roleName: string; description: string; permissions: Array<{ permName: string; allow: boolean }> }> }>(
      'getSiteRolePermissions', { siteObjKey }),

  /** Save customised role permissions for a site */
  saveSiteRolePermissions: (siteObjKey: string, roles: Array<{ roleObjKey: string; permissions: Array<{ permName: string; allow: boolean }> }>) =>
    rpcCall<boolean>('saveSiteRolePermissions', { siteObjKey, roles }),

  /** Create a new access group on a site */
  addAccessGroup: (siteObjKey: string, groupId: string, groupName: string, groupType?: string) =>
    rpcCall<{ objKey: string; id: string; name: string; type: string }>('addAccessGroup', { siteObjKey, groupId, groupName, groupType }),

  /** Rename an existing access group */
  updateAccessGroup: (siteObjKey: string, groupObjKey: string, groupName: string) =>
    rpcCall<boolean>('updateAccessGroup', { siteObjKey, groupObjKey, groupName }),

  /** Delete an access group and clean up member ACLs */
  deleteAccessGroup: (siteObjKey: string, groupObjKey: string) =>
    rpcCall<boolean>('deleteAccessGroup', { siteObjKey, groupObjKey }),

  // ── Sprint 2 — Study Flags & Diagnostic Info ───────────────

  /** Mark study as Important Finding */
  markImportant: (params: {
    objKey: string;
    isImportant?: boolean;
    reason?: string;
    actionNeeded?: boolean;
    note?: string;
    phone?: string;
    viewed?: boolean;
    ackTime?: number;
  }) => rpcCall<Study>('markImportant', params),

  /** Mark study as Follow-Up Recommended */
  markFollowUp: (params: {
    objKey: string;
    isFollowUp?: boolean;
    reason?: string;
    actionNeeded?: boolean;
    note?: string;
    phone?: string;
    viewed?: boolean;
    remindTime?: number;
    remindUnits?: string;
    isRemindPhy?: boolean;
    isRemindPatient?: boolean;
  }) => rpcCall<Study>('markFollowUp', params),

  /** Get DICOM header tag dump for an object */
  getDICOMDumpInfo: (stuKey: string, objKey: string) =>
    rpcCall<string>('getDICOMDumpInfo', { stuKey, objKey }),

  /** Get HTML-formatted Structured Report */
  getStructuredReport: (stuKey: string, objKey: string) =>
    rpcCall<string>('getStructuredReport', { stuKey, objKey }),

  /** Get linked/prior studies */
  getLinkedStudies: (objKey: string, includeTarget?: boolean) =>
    rpcCall<{ records: Study[]; total: number }>('getLinkedStudies', { objKey, includeTarget }),

  /** Get remote studies with local comparison */
  getRemoteStudiesWithLocalComparison: (patientID: string, siteKey: string) =>
    rpcCall<{ records: Study[]; total: number }>('getRemoteStudiesWithLocalComparison', { patientID, siteKey }),

  // ── Sprint 3 — QC & Object Management ──────────────────────────

  /** Generate an accession number for a study */
  generateAccNumber: (stuKey: string) =>
    rpcCall<Study>('generateAccNumber', { stuKey }),

  /** Update label and comment on an attachment/object */
  updateLabelAndComment: (parentObjKey: string, attachObjKey: string, label: string, comment?: string) =>
    rpcCall<DicomObject>('updateLabelAndComment', { parentObjKey, attachObjKey, label, comment }),

  /** Assign an object/attachment to a study */
  assignObjectToStudy: (params: {
    stuKey: string;
    assignObjectKey: string;
    label: string;
    comment?: string;
    selectedPages?: number[];
  }) => rpcCall<DicomObject>('assignObjectToStudy', params),

  /** Unassign an object from a study */
  unassignObjectFromStudy: (objKey: string, stuKey: string) =>
    rpcCall<DicomObject | boolean>('unassignObjectFromStudy', { objKey, stuKey }),

  /** Delete objects and release locks */
  deleteAndUnlockObjects: (stuKey: string, objKeys: string[], lockObjKeys?: string[]) =>
    rpcCall<boolean>('deleteAndUnlockObjects', { stuKey, objKeys, lockObjKeys }),

  /** Bulk fetch studies by key set */
  getStudySet: (stuKeys: string[]) =>
    rpcCall<{ records: Study[]; total: number }>('getStudySet', { stuKeys }),

  /** Bulk fetch objects for multiple studies */
  getObjectsSet: (stuKeys: string[]) =>
    rpcCall<{ records: DicomObject[]; total: number }>('getObjectsSet', { stuKeys }),

  /** Get study objects with comparison data for viewer */
  getStudyObjects: (params: {
    studyKey: string;
    isRemote?: boolean;
    siteKey?: string;
    uid?: string;
    id?: string;
    acc?: string;
    rsbKey?: string;
  }) => rpcCall<{
    records: DicomObject[];
    total: number;
    rsbKey?: string;
    comparisonStudies?: Study[];
  }>('getStudyObjects', params),

  /** Full study field update */
  updateStudy: (params: {
    objKey: string;
    patObjKey: string;
    acc: string;
    descr?: string;
    mod?: string;
    bod?: string;
    phy?: string;
    priority?: string;
    reason?: string;
    history?: string;
    comment?: string;
    ordLoc?: string;
    location?: string;
    backupContact?: string;
    cpt?: { procCode: string; codeDesig?: string; codeMeaning?: string; minTime?: number };
  }) => rpcCall<Study>('updateStudy', params),

  /** Update patient info on a study */
  updateStudyPatientInfo: (studyObjKey: string, patientObjKey: string, patient?: {
    name?: string; id?: string; dob?: string; sex?: string;
  }) => rpcCall<Study>('updateStudyPatientInfo', { studyObjKey, patientObjKey, patient }),

  /** Download (C-MOVE) a study from a remote server */
  downloadStudy: (params: {
    objKey?: string;
    isRemote?: boolean;
    remoteStudyUid?: string;
    patId?: string;
    patName?: string;
    site?: string;
    rsbKey?: string;
    accNum?: string;
    matchedPatientKey?: string;
  }) => rpcCall<Study & { downloadRemoteServer?: string; downloadStudyUID?: string }>('downloadStudy', params),

  // ── Sprint 4: User Administration ──

  /** Load user config JSON */
  getUserConfig: (userKey: string) => rpcCall<Record<string, unknown>>('getUserConfig', { userKey }),

  /** Save user + config together (or config only) */
  saveUserAndConfig: (params: {
    userKey?: string;
    config?: string;
    user?: { objKey: string; userName?: string; userId?: string; dicomAe?: string; dicomHost?: string; dicomPort?: number | string; details?: string; schedule?: string };
  }) => rpcCall<boolean>('saveUserAndConfig', params),

  /** Typeahead autocomplete for user fields */
  getUserSuggestions: (params: {
    query?: string;
    limit?: number;
    start?: number;
    site?: string;
    role?: string;
    token?: string;
    suggestionType?: 'NAME_ID' | 'NPI_NAME_ID' | 'EMAIL_NAME_ID' | 'FAX_NAME_ID';
  }) => rpcCall<Array<{ displayString: string; objKey: string }>>('getUserSuggestions', params),

  /** Pre-validate user ID/name/AE conflicts before save */
  checkPossibilityAddUpdateUser: (siteKey: string, id: string, name: string, roleName: string, isAddedUser: boolean) =>
    rpcCall<boolean>('checkPossibilityAddUpdateUser', { siteKey, id, name, roleName, isAddedUser }),

  /** AE title conflict check */
  getUsersWithSameAE: (objKey: string, site: string, ae: string) =>
    rpcCall<User[]>('getUsersWithSameAE', { objKey, site, ae }),

  /** Users filtered by group with pagination */
  getUsersPagedForGroup: (params: {
    offset?: number;
    limit?: number;
    modalityOnly?: boolean;
    locationOnly?: boolean;
    physicianOnly?: boolean;
    searchName?: string;
    siteObjKey?: string;
    userObjKey?: string;
  }) => rpcCall<{ records: User[]; offset: number; total: number }>('getUsersPagedForGroup', params),

  /** Pre-delete reference check for a user */
  hasUserReferences: (objKey: string) =>
    rpcCall<{ studies: number; orders: number; reports: number; hasReferences: boolean }>('hasUserReferences', { objKey }),

  /** Count assigned studies for a user */
  getAssignedStudiesCount: (userObjKey: string) =>
    rpcCall<number>('getAssignedStudiesCount', { userObjKey }),

  /** Release all studies assigned to a user */
  releaseStudies: (userObjKey: string) => rpcCall<boolean>('releaseStudies', { userObjKey }),

  /** Lock a user account */
  putLockOnUser: (objKey: string) => rpcCall<boolean>('putLockOnUser', { objKey }),

  /** Unlock a user account */
  removeLockFromUser: (objKey: string) => rpcCall<boolean>('removeLockFromUser', { objKey }),

  /** Set password expiry flag (root admin only) */
  changePasswordNeverExpires: (objKey: string, pwNeverExpires: boolean) =>
    rpcCall<User>('changePasswordNeverExpires', { objKey, pwNeverExpires }),

  /** Full password reset flow with token validation */
  resetPassword: (params: {
    userKey: string;
    pw: string;
    def?: boolean;
    questionIndex?: number;
    answer?: string;
    userSchedule?: string;
    answers?: string;
    userToken: string;
  }) => rpcCall<boolean>('resetPassword', params),

  /** Validate a password reset token */
  checkResetToken: (siteId: string, userId: string, userToken: string) =>
    rpcCall<User>('checkResetToken', { siteId, userId, userToken }),

  /** Validate a security question answer */
  checkSecurityAnswer: (userKey: string, questionIndex: number, answer: string, userToken: string) =>
    rpcCall<boolean>('checkSecurityAnswer', { userKey, questionIndex, answer, userToken }),

  /** Audit log entry for password reset */
  logResetPasswordMessage: (level: string, message: string, recordDetail: string) =>
    rpcCall<boolean>('logResetPasswordMessage', { level, message, recordDetail }),

  // ── Sprint 5 — Site Administration & Validation ──

  /** Get site(s) by object key(s) */
  getSite: (objKeys: string[]) =>
    rpcCall<{ sites: Array<Record<string, unknown>> }>('getSite', { objKeys }),

  /** Pre-delete reference check for a site */
  hasSiteReferences: (objKey: string) =>
    rpcCall<{ hasReferences: boolean; users: number; modalities: number; locations: number; studies: number; patients: number; enterpriseChildren: number }>('hasSiteReferences', { objKey }),

  /** Validate site info — check duplicate IDs, AE titles, phones */
  validateSiteInfo: (id: string | null, objKey: string | null, aeTitles: string[], phaxioPhones?: string) =>
    rpcCall<{ siteIdDuplicate: string | null; aeDuplicates: string; phoneDuplicate: string | null }>('validateSiteInfo', { id, objKey, aeTitles, phaxioPhones }),

  /** Validate HL7 inbound host+sendingFacility uniqueness */
  validateInbound: (siteObjKey: string | null, siteHosts: Array<{ host: string; sendingFacility?: string }>, host: string, sendingFacility?: string) =>
    rpcCall<{ valid: boolean; message?: string }>('validateInbound', { siteObjKey, siteHosts, host, sendingFacility }),

  /** Get sites that have users (for dropdowns) */
  getSitesOfUsers: () =>
    rpcCall<{ sites: Array<{ objKey: string; name: string }>; total: number }>('getSitesOfUsers', {}),

  /** Check if child sites auto-add procedure codes from enterprise parent */
  isAutoAddProcCodesOnChildSites: (siteObjKey: string) =>
    rpcCall<boolean>('isAutoAddProcCodesOnChildSites', { siteObjKey }),

  /** Get access groups, optionally filtered by site */
  getGroups: (siteKey?: string) =>
    rpcCall<{ records: Array<{ objKey: string; id: string; name: string; type: string; acls: string }>; total: number }>('getGroups', { siteKey }),

  // ── Sprint 6 — Scheduling & Orders ──────────────────────────────

  /** Get the patient for an order/study */
  getOrderPatient: (studyObjKey: string) =>
    rpcCall<Record<string, unknown> | null>('getOrderPatient', { studyObjKey }),

  /** Set extended time on an order */
  setOrderExtendedTime: (studyKey: string, extTimeTop: number, extTimeBottom: number) =>
    rpcCall<Record<string, unknown> | null>('setOrderExtendedTime', { studyKey, extTimeTop, extTimeBottom }),

  /** Full order update (study + order info + patient + notes + CDS events) */
  updateOrder: (params: {
    study?: Record<string, unknown>;
    order?: Record<string, unknown>;
    patientKey?: string;
    orderNote?: { type?: string; reason?: string };
    cdsEvents?: Array<Record<string, unknown>>;
  }) =>
    rpcCall<Record<string, unknown> | null>('updateOrder', params),

  /** Find available time slots matching constraints */
  findTimeSlots2: (params: {
    wheres?: Array<{ attr: string; op: string; sval?: string[]; dval?: string[] }>;
    aeToMinTime?: Record<string, number>;
    calculateAllTime?: boolean;
  }) =>
    rpcCall<Record<string, unknown> | null>('findTimeSlots2', params),

  /** Block/unblock/reserve/unreserve time slots */
  setTimeSlotsStatus: (actions: Array<{
    status: 'BLOCKED' | 'UNBLOCKED' | 'RESERVED' | 'UNRESERVED';
    slots: Array<Record<string, unknown>>;
    blockReason?: string;
    blockOnlyFreeSlots?: boolean;
    modObjKeyToMove?: string;
    studyKey?: string;
  }>) =>
    rpcCall<Array<Record<string, unknown>>>('setTimeSlotsStatus', { actions }),

  /** Get blocking history for a time slot */
  getBlockingHistory: (slot: Record<string, unknown>) =>
    rpcCall<Array<{ action: string; date: string; user: string; reason: string }>>('getBlockingHistory', { slot }),

  // ─── Sprint 7 — Procedures & Body Parts ────────────────────────────

  /** Get all distinct body parts */
  getBodyParts: () => rpcCall<string[]>('getBodyParts', {}),

  /** Get system-wide default procedure definitions */
  getDefaultProcedureDefinitions: () =>
    rpcCall<{ records: ProcedureDefinition[] }>('getDefaultProcedureDefinitions', {}),

  /** Get procedure definitions for a specific site */
  getProcedureDefinitionsForSite: (
    siteObjKey: string,
    noDefault = false,
    customizedOnly = false,
  ) =>
    rpcCall<{ records: ProcedureDefinition[] }>('getProcedureDefinitionsForSite', {
      siteObjKey,
      noDefault,
      customizedOnly,
    }),

  /** Get procedures available to the current user */
  getProcedureDefinitionsForCurrentUser: () =>
    rpcCall<{ records: ProcedureDefinition[] }>('getProcedureDefinitionsForCurrentUser', {}),

  /** Get procedures by modality (plus site procedures) */
  getProcedureDefinitionsByModality: (objKey?: string) =>
    rpcCall<{ procedures: ProcedureDefinition[]; siteProcedures: ProcedureDefinition[] }>(
      'getProcedureDefinitionsByModality',
      { objKey },
    ),

  /** Update customized procedures for a modality */
  updateProcedureDefinitionsForModality: (
    modalityKey: string,
    procedures: ProcedureDefinition[],
  ) =>
    rpcCall<boolean>('updateProcedureDefinitionsForModality', { modalityKey, procedures }),

  // ─── Sprint 8 — HL7, Messages, Fax, System ─────────────────────────

  /** Get parsed HL7 message dump */
  getHL7DumpInfo: (objKey: string) =>
    rpcCall<HL7DumpBean>('getHL7DumpInfo', { objKey }),

  /** Bulk resend HL7 messages by date range and type */
  resendBulkHL7Messages: (
    siteObjKey: string | null,
    connectionObjKey: string | null,
    startDate: number,
    endDate: number,
    types: string[],
    preview = false,
  ) =>
    rpcCall<boolean>('resendBulkHL7Messages', {
      siteObjKey,
      connectionObjKey,
      startDate,
      endDate,
      types,
      preview,
    }),

  /** Add test HL7 headers to a site */
  addSampleHeaders: (siteKey: string, headers: string) =>
    rpcCall<boolean>('addSampleHeaders', { siteKey, headers }),

  /** Mark messages as read */
  ackMessages: (objKeys: string[]) =>
    rpcCall<Message[]>('ackMessages', { objKeys }),

  /** Delete messages by key */
  deleteMessages: (objKeys: string[]) =>
    rpcCall<boolean>('deleteMessages', { objKeys }),

  /** Initiate a fax send */
  sendFax: (
    objectId: string,
    documentType: string,
    studyKey: string,
    sendToPhy = false,
    sendToAttPhy = false,
    sendToTech = false,
    sendToLocation = false,
    destinations: string[] = [],
  ) =>
    rpcCall<boolean>('sendFax', {
      objectId,
      documentType,
      studyKey,
      sendToPhy,
      sendToAttPhy,
      sendToTech,
      sendToLocation,
      destinations,
    }),

  /** Update fax contact info on a notification */
  updateNotificationFax: (
    notificationObjKey: string,
    contact: string | null,
    recipient: string | null,
    userKey: string | null,
  ) =>
    rpcCall<boolean>('updateNotificationFax', {
      notificationObjKey,
      contact,
      recipient,
      userKey,
    }),

  /** Test fax device ports */
  echoFaxDevice: (ports: string) =>
    rpcCall<Record<string, boolean | null>>('echoFaxDevice', { ports }),

  /** Acknowledge an emergency (fire drill) message */
  ackEmergencyMessage: (emergencyKey: string) =>
    rpcCall<boolean>('ackEmergencyMessage', { emergencyKey }),

  /** Write system properties with validation */
  setProperties: (properties: string[], values: (string | null)[]) =>
    rpcCall<boolean>('setProperties', { properties, values }),

  /** Get system activity report chart */
  getChart: (params: {
    reportName?: string;
    siteObjKey?: string;
    startDate?: number;
    endDate?: number;
    jsonData?: string;
  }) => rpcCall<SystemActivityReportData>('getChart', params),

  /** Get session audit logs */
  getSessionLogs: (text?: string) =>
    rpcCall<SessionLogEntry[]>('getSessionLogs', { text }),

  /** Get individual log records for a session */
  getLogRecords: (objKey: string) =>
    rpcCall<PacsLogEntry[]>('getLogRecords', { objKey }),

  /** Search all sessions for records matching text (case-sensitive) */
  searchLogRecords: (text: string) =>
    rpcCall<PacsLogEntry[]>('searchLogRecords', { text }),

  /** Get study audit trail */
  getTransactions: (itemKey?: string, itemParent?: string) =>
    rpcCall<StudyTransactionEntry[] | null>('getTransactions', { itemKey, itemParent }),

  /** Get default saved searches per site */
  getDefaultSearchesBySite: (siteKey: string) =>
    rpcCall<Record<string, SavedSearch> | null>('getDefaultSearchesBySite', { siteKey }),

  /** Save report format settings for a site */
  saveReportSettings: (siteKey: string, settings: string) =>
    rpcCall<boolean>('saveReportSettings', { siteKey, settings }),

  /** Get report format settings for a site/report type */
  getSavedReportSettings: (siteKey: string, reportTypeName?: string) =>
    rpcCall<string | null>('getSavedReportSettings', { siteKey, reportTypeName }),

  // ─── Sprint 9 — HIPAA Audit Reports ────────────────────────────

  /** Generate patient access audit report (HIPAA compliance) */
  getAccessReport: (
    patientKey?: string,
    userKey?: string,
    startDate?: number,
    endDate?: number,
  ) =>
    rpcCall<string | null>('getAccessReport', { patientKey, userKey, startDate, endDate }),

  // ─── P2 Nice-to-Haves ──────────────────────────────────────────

  // ── Reading Location ──
  /** Get user's current reading location */
  getCurrentReadingLocation: () =>
    rpcCall<ReadingLocation | null>('getCurrentReadingLocation', {}),

  /** Get available reading locations */
  getAvailableReadingLocations: (onlyPrivate?: boolean) =>
    rpcCall<ReadingLocation[]>('getAvailableReadingLocations', { onlyPrivate }),

  /** Save current reading location to session */
  saveCurrentLocationToSession: (location?: ReadingLocation) =>
    rpcCall<boolean>('saveCurrentLocationToSession', location || {}),

  /** Get missed SLA reasons for current site */
  getSiteMissedSlaReasons: () =>
    rpcCall<MissedSlaCategory[]>('getSiteMissedSlaReasons', {}),

  // ── Export / Download ──
  /** Prepare data for grid export (CSV, etc.) */
  prepareDataForExport: (request: GridExportRequest) =>
    rpcCall<string | null>('prepareDataForExport', { request: JSON.stringify(request) }),

  /** Prepare studies for CD/ZIP download */
  prepareToDownloadForCD: (studyKeys: string[], opts?: CDPreparationOptions, downloadSessionKey?: string) =>
    rpcCall<boolean>('prepareToDownloadForCD', { studyKeys, ...opts, downloadSessionKey }),

  /** Terminate a DICOMDIR session */
  terminateDicomDirSession: (dicomDirSessionKey: string) =>
    rpcCall<boolean>('terminateDicomDirSession', { dicomDirSessionKey }),

  /** Get download progress for a study */
  getDownloadProgressInfo: (studyKey: string) =>
    rpcCall<DownloadProgressInfo | null>('getDownloadProgressInfo', { studyKey }),

  // ── Patient Advanced ──
  /** Find patients for merge */
  findPatientsForMerge: (objKey: string, newId: string) =>
    rpcCall<PatientBean[]>('findPatientsForMerge', { objKey, newId }),

  /** Merge two patients */
  mergePatients: (basePatientKey: string, patientKeyForMerge: string, mergedPatient?: MergedPatientData) =>
    rpcCall<PatientBean | null>('mergePatients', { basePatientKey, patientKeyForMerge, mergedPatient }),

  /** Update patient mapping (VNA) */
  updatePatientMapping: (patientObjKey: string, mappings: PatientMappingEntry[]) =>
    rpcCall<PatientBean | null>('updatePatientMapping', { patientObjKey, mappings }),

  /** Get patient photo image */
  getPatientPhotoImage: (patientKey: string) =>
    rpcCall<PatientObjectItem | null>('getPatientPhotoImage', { patientKey }),

  /** Upload patient photo capture */
  uploadPatientCapture: (patientKey: string, site: string, photoBase64: string, isUpdatePatientImage?: boolean) =>
    rpcCall<string | null>('uploadPatientCapture', { patientKey, site, photoBase64, isUpdatePatientImage }),

  /** Get insurance card photo */
  getInsurancePhotoImage: (patientKey: string, insuranceObjKey: string, insuranceCardType?: string) =>
    rpcCall<PatientObjectItem | null>('getInsurancePhotoImage', { patientKey, insuranceObjKey, insuranceCardType }),

  /** Generate a new patient object key */
  generatePatientObjectKey: (site: string) =>
    rpcCall<string | null>('generatePatientObjectKey', { site }),

  /** Search remote patients */
  getRemotePatients: (offset?: number, limit?: number, where?: WhereClause[]) =>
    rpcCall<RemotePatientResult>('getRemotePatients', { offset, limit, where }),

  /** Get insurance company list for a site */
  getInsuranceCompanyList: (siteObjKey: string, opts?: { fromEnterpriseSite?: boolean; insuranceCompanyNameQuery?: string; exactMatch?: boolean; limit?: number }) =>
    rpcCall<InsuranceCompanyBean[]>('getInsuranceCompanyList', { siteObjKey, ...opts }),

  /** Add/update an insurance company */
  addInsuranceCompany: (siteObjKey: string, insuranceCompanyBean: InsuranceCompanyBean) =>
    rpcCall<InsuranceCompanyBean | null>('addInsuranceCompany', { siteObjKey, insuranceCompanyBean }),

  // ── Hanging Protocols ──
  /** Get hanging protocols (summary) */
  getProtocols: (parentKey?: string) =>
    rpcCall<Record<string, ProtocolBean>>('getProtocols', { parentKey }),

  /** Get hanging protocols with full detail and optional scope */
  getProtocolsFull: (params?: { scope?: 'user' | 'site' | 'all' }) =>
    rpcCall<Record<string, BritProtocol>>('getProtocols', { detail: true, ...params }),

  /** Save a hanging protocol (summary — backward compat) */
  saveProtocol: (protocol: ProtocolBean, parentKey?: string) =>
    rpcCall<boolean>('saveProtocol', { parentKey, protocol }),

  /** Save a full hanging protocol with optional scope */
  saveProtocolFull: (protocol: BritProtocol, scope?: 'user' | 'site') =>
    rpcCall<boolean>('saveProtocol', { protocol, scope }),

  /** Delete a hanging protocol */
  deleteProtocol: (id: string, parentKey?: string) =>
    rpcCall<boolean>('deleteProtocol', { parentKey, id }),

  /** Delete a hanging protocol with scope */
  deleteProtocolScoped: (id: string, scope: 'user' | 'site') =>
    rpcCall<boolean>('deleteProtocol', { id, scope }),

  // ── QC Operations ──
  /** Send a QC command */
  sendQCCommand: (commandBean: QCCommandRequest) =>
    rpcCall<QCCommandResult>('sendQCCommand', { commandBean }),

  /** Restore unassigned objects */
  restoreUnassignedObjects: (objKeys: string[]) =>
    rpcCall<boolean>('restoreUnassignedObjects', { objKeys }),

  /** Get window/level for images */
  getImageWL: (studyObjectKey: string, imageObjectKeys: string[]) =>
    rpcCall<number[][]>('getImageWL', { studyObjectKey, imageObjectKeys }),

  /** Set calibration parameters for objects */
  setCalibrationParams: (studyKey: string, objKeys: string[], calibrationCoeff: number, calibrationUnit: string) =>
    rpcCall<boolean>('setCalibrationParams', { studyKey, objKeys, calibrationCoeff, calibrationUnit }),

  // ── Sharing ──
  /** Get sharing entries for a study */
  getSharingBeans: (studyKey: string) =>
    rpcCall<SharingBean[]>('getSharingBeans', { studyKey }),

  // ── Macros & AI ──
  /** Convert macros to exportable string */
  convertMacrosToString: (exportMacros: Array<{ objKey?: string; name?: string; content?: string }>) =>
    rpcCall<MacrosExportResult>('convertMacrosToString', { exportMacros }),

  /** Export AI validation matches */
  exportAIValidationMatches: (matches: AIValidationMatch[]) =>
    rpcCall<MacrosExportResult>('exportAIValidationMatches', { matches }),

  // ── Fire Drill ──
  /** Get fire drill report (paged) */
  getFireDrillReportPaged: (params: {
    parentKey?: string; objId?: string; offset?: number; limit?: number;
    showSuccess?: boolean; showFailed?: boolean; showPending?: boolean;
    searchName?: string; searchPhone?: string; searchFailed?: string;
  }) =>
    rpcCall<FireDrillReport | null>('getFireDrillReportPaged', params),

  /** Retry specific failed fire drill users */
  retryFireDrillFailed: (siteKey: string, drillId: string, failedUsers: Array<{ objKey?: string }>) =>
    rpcCall<boolean>('retryFireDrillFailed', { siteKey, drillId, failedUsers }),

  /** Retry all failed fire drill users */
  retryFireDrillAllFailed: (siteKey: string, drillId: string, retrySelective?: boolean) =>
    rpcCall<boolean>('retryFireDrillAllFailed', { siteKey, drillId, retrySelective }),

  // ── System Labels ──
  /** Save system labels settings (Root Admin only) */
  saveSystemLabelsSettings: (settings: string) =>
    rpcCall<boolean>('saveSystemLabelsSettings', { settings }),

  /** Get system labels settings */
  getSystemLabelsSettings: () =>
    rpcCall<Record<string, unknown> | null>('getSystemLabelsSettings', {}),

  // ── Communication ──
  /** Send command to a user */
  sendCommand: (to: string, text?: string, code?: string, itemKeys?: string[], isOnlyForMySession?: boolean) =>
    rpcCall<CommandMessage | null>('sendCommand', { to, text, code, itemKeys, isOnlyForMySession }),

  /** Send command to all users viewing a study */
  sendCommandToAllViewingUsers: (studyObjKey: string, lockObjKey?: string, text?: string, code?: string, notSendToThem?: string[]) =>
    rpcCall<boolean>('sendCommandToAllViewingUsers', { studyObjKey, lockObjKey, text, code, notSendToThem }),

  /** Send upload request notification */
  sendUploadRequestNotification: (to: string, notificationType: string, originalStudyKey?: string, studyKey?: string) =>
    rpcCall<boolean>('sendUploadRequestNotification', { to, notificationType, originalStudyKey, studyKey }),

  /** Send prep notes email to patient */
  sendMailWithPrepNotesToPatient: (email: string, procedureObjKey: string, studyObjKey: string) =>
    rpcCall<boolean>('sendMailWithPrepNotesToPatient', { email, procedureObjKey, studyObjKey }),

  // ── Diagnostic ──
  /** HTTP echo/ping test */
  httpEcho: (params: {
    siteKey?: string; server?: string; port?: string; httpPath?: string;
    commandVerb?: string; useHTTPS?: boolean; user?: string; pw?: string;
  }) =>
    rpcCall<boolean>('httpEcho', params),

  /** Get client IP address */
  echoIP: () =>
    rpcCall<string | null>('echoIP', {}),

  /** Test connection to server */
  testConnection: (siteKey?: string, userKey?: string, sessionUuid?: string) =>
    rpcCall<TestConnectionResult | null>('testConnection', { siteKey, userKey, sessionUuid }),

  /** Test LDAP connection */
  testLdapConnection: (ldapUrl: string, ldapDomain: string) =>
    rpcCall<boolean>('testLdapConnection', { ldapUrl, ldapDomain }),

  /** Get login configuration */
  getLoginConfig: (params?: Record<string, unknown>) =>
    rpcCall<LoginConfig | null>('getLoginConfig', params || {}),

  // ── Signature Images ──
  /** Get a user's signature image */
  getSignatureImage: (userObjKey: string) =>
    rpcCall<{ image: string | null; mimeType?: string }>('getSignatureImage', { userObjKey }),

  /** Upload a user's signature image (base64) */
  uploadSignatureImage: (userObjKey: string, image: string, mimeType?: string) =>
    rpcCall<boolean>('uploadSignatureImage', { userObjKey, image, mimeType: mimeType ?? 'image/png' }),

  /** Remove a user's signature image */
  removeSignatureImage: (userObjKey: string) =>
    rpcCall<boolean>('removeSignatureImage', { userObjKey }),

  // ── Fire Drill History ──
  /** Get list of scheduled fire drills for a site */
  getFireDrillHistory: (siteObjKey: string) =>
    rpcCall<FireDrillHistoryEntry[]>('getFireDrillHistory', { siteObjKey }),

  // ── Phase 7 Sprint C — User ACL management ──

  /** Update a user's ACL entries (Site Access, Access Groups, Custom Roles) */
  updateUserAcl: (userObjKey: string, addAcl: string[], removeAcl: string[]) =>
    rpcCall<User>('updateUserAcl', { userObjKey, addAcl, removeAcl }),

  /** Get custom roles from site permissions (objKey prefix "crole:") */
  getCustomRoles: (siteObjKey?: string) =>
    rpcCall<{ records: Array<{ objKey: string; id: string; name: string; siteKey: string; siteName: string }>; total: number }>('getCustomRoles', { siteObjKey }),
};
